/** Welche Spielaktionen und Spielverläufe als TelemetryDeck-Signale gezählt werden. Nur Kennzahlen, keine Texte. */
import { useEffect, useRef } from 'react';
import { S } from '../engine/state';
import { poll } from '../engine/govern';
import { track } from './telemetry';

const mode = () => (S.mp && S.mp.mode === 'versus' ? 'versus' : S.mp ? 'coop' : 'single');
let lastAction = 0;
/** Zeitpunkt der letzten Aktion (lokal oder vom Mitspieler). Phasenwechsel ohne Aktion (Laden eines Spielstands) zählen nicht. */
export const markAction = () => { lastAction = Date.now(); };

/** Zustand vor einer Aktion, den das Signal braucht (danach ist er oft schon weg). */
export function beforeAction(): { phase: string; editing: any; crisis: string | null; dec: string | null; first: boolean; camp: { first: boolean; budget?: number; spent: number } | null } {
  const c: any = S?.camp;
  return { camp: c ? { first: !!c.first, budget: c.fund?.total, spent: Object.values<number>(c.ads || {}).reduce((x, v) => x + v, 0) } : null, phase: S?.phase, editing: S?.editing ? { iss: S.editing.iss, tpl: S.editing.tpl, rework: !!S.editing.lawId } : null, crisis: S?.crisis?.id ?? null, dec: S?.dec?.id ?? null, first: !!S?.mp?.first };
}

/** Nach einer lokalen Aktion: passendes Signal senden. */
export function trackAction(name: string, args: unknown[], b: ReturnType<typeof beforeAction>) {
  markAction();
  if (!S) return;
  switch (name) {
    case 'campStart': if (b.phase === 'program') track('Game.started', { mode: 'single', difficulty: S.cfg?.diff }); break;
    case 'mpCampStart': if (b.phase === 'program') track('Game.started', { mode: 'versus' }); break;
    case 'campDone': case 'mpCampDone': {
      const c = name === 'campDone' ? b.camp : null;
      track('Campaign.finished', { mode: mode(), first: name === 'campDone' ? !!c?.first : b.first, adsBudget: c?.budget, adsSpent: c?.spent });
      break; }
    case 'commission': if (b.editing) track(b.editing.rework ? 'Law.reworked' : 'Law.commissioned', { issue: b.editing.iss, approach: b.editing.tpl, mode: mode() }); break;
    case 'p2Propose': track('Law.commissioned', { issue: String(args[0]), approach: Number(args[1]), mode: mode(), by: 'partner' }); break;
    case 'crisis': if (b.crisis) track('Crisis.decided', { crisis: b.crisis, option: Number(args[0]) }); break;
    case 'decide': if (b.dec) track('Decision.made', { decision: b.dec, option: Number(args[0]) }); break;
    case 'repeal': track('Law.repealed', { mode: mode() }); break;
    case 'brDeal': track('Parliament.landerDeal', {}); break;
    case 'snapElection': if (b.phase === 'govern') track('Game.earlyElection', { mode: mode() }); break;
    case 'reposition': track('Program.adjusted', {}); break;
    case 'setDiff': track('Settings.difficultyChanged', { difficulty: String(args[0]) }); break;
    case 'oppPropose': track('Opposition.bill', {}); break;
    case 'oppSue': track('Opposition.lawsuit', {}); break;
    case 'oppAttack': track('Opposition.debate', {}); break;
    case 'oppMisstrauen': track('Opposition.noConfidenceVote', {}); break;
    case 'partnerQuit': track('Coalition.partnerQuit', {}); break;
  }
}

/** Spielverlauf beobachten: Wahl, Regierungsbildung, Ende der Legislatur, Sturz der Regierung. */
export function useOutcomeTracking() {
  const prev = useRef<string | null>(null);
  const phase = S?.phase ?? null;
  useEffect(() => {
    const p = prev.current; prev.current = phase;
    if (!p || !phase || p === phase || Date.now() - lastAction > 5000) return;
    const m = mode();
    if (phase === 'night' && p === 'campaign') {
      const you = S.res?.share?.YOU;
      track('Game.electionHeld', { mode: m, first: true, share: you != null ? Math.round(you) : undefined, seats: S.res?.seats?.YOU });
    } else if (phase === 'govern' && (p === 'night' || p === 'talks')) {
      track('Game.governmentFormed', { mode: m, partners: S.partners.length, minority: !!S.minority, legislature: S.leg + 1 });
    } else if (phase === 'review') {
      track('Game.legislatureCompleted', { mode: m, legislature: S.leg + 1, laws: S.review?.laws?.length, poll: Math.round(poll()) });
    } else if (phase === 'end') {
      if (S.end === 'election') track('Game.reelection', { mode: m, won: !!S.won, share: Math.round(S.final || 0), legislature: S.leg + 1 });
      else track('Game.governmentFell', { mode: m, reason: S.end || 'unknown', legislature: S.leg + 1 });
    } else if (phase === 'campaign' && p !== 'program') {
      track('Campaign.started', { mode: m, legislature: S.leg + 1, early: !!S.early });
    }
  }, [phase]);
}
